/**
 * Map Styles Configuration
 *
 * Single Responsibility: Defines Google Maps styling configuration
 * Matches Figma design with neutral beige/cream tones
 */

import { colors } from '@/design-system/tokens/colors';

/**
 * Google Maps style configuration matching Figma design
 * Uses neutral beige/cream for land areas (no yellow, green, or blue tints)
 */
export const mapStyles: google.maps.MapTypeStyle[] = [
  // Set base color for all geometry first - this ensures all land areas get the color
  {
    featureType: 'all',
    elementType: 'geometry',
    stylers: [{ color: '#F5F5F4' }], // Neutral beige/cream - matches Figma
  },
  // Keep labels visible as per design
  {
    featureType: 'all',
    elementType: 'labels',
    stylers: [{ visibility: 'on' }],
  },
  // Explicitly set landscape/land areas to ensure correct color
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#F5F5F4' }], // Neutral beige/cream
  },
  {
    featureType: 'landscape',
    elementType: 'geometry.fill',
    stylers: [{ color: '#F5F5F4' }], // Ensure fill color matches
  },
  // Natural features (parks, forests) - use same neutral color (no green tint)
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#F5F5F4' }], // Same neutral beige - no green
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry.fill',
    stylers: [{ color: '#F5F5F4' }],
  },
  // Water bodies - very muted, desaturated blue
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#E5E7EB' }], // Very muted, almost grey-blue
  },
  {
    featureType: 'water',
    elementType: 'geometry.fill',
    stylers: [{ color: '#E5E7EB' }],
  },
  // Water labels - more neutral grey
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#78716C' }], // Neutral grey - less blue
  },
  // Roads - white or very light
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#FFFFFF' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [{ color: '#FFFFFF' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#F5F5F4' }], // Match base neutral beige for road edges
  },
  // Road labels - dark gray/black text
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: colors.typography.primary }], // #1C1917 - dark gray/black
  },
  {
    featureType: 'road',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#FFFFFF' }, { weight: 2 }], // White stroke for readability
  },
  // Administrative boundaries - subtle strokes
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: colors.stroke.light }, { weight: 0.5 }], // #E7E5E4 - subtle
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.fill',
    stylers: [{ color: '#F5F5F4' }], // Same as landscape - neutral beige
  },
  {
    featureType: 'administrative.locality',
    elementType: 'geometry',
    stylers: [{ color: '#F5F5F4' }],
  },
  {
    featureType: 'administrative.neighborhood',
    elementType: 'geometry',
    stylers: [{ color: '#F5F5F4' }],
  },
  // Points of interest - hide labels but keep geometry with base color
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#F5F5F4' }], // Use base neutral beige color
  },
  // Transit - minimal styling with subtle colors
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: colors.stroke.base }], // #D6D3D1
  },
  // Transit stations - hide labels
  {
    featureType: 'transit.station',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  // Remove any green tints from natural features
  {
    featureType: 'poi.attraction',
    elementType: 'geometry',
    stylers: [{ color: '#F5F5F4' }], // Neutral beige - no green
  },
  {
    featureType: 'poi.business',
    elementType: 'geometry',
    stylers: [{ color: '#F5F5F4' }], // Neutral beige
  },
  // Ensure no green tints in any natural/landscape features
  {
    featureType: 'landscape.natural',
    elementType: 'geometry',
    stylers: [{ color: '#F5F5F4' }], // Neutral beige - no green
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry.fill',
    stylers: [{ color: '#F5F5F4' }],
  },
];

/**
 * Default map options configuration
 */
export const defaultMapOptions: google.maps.MapOptions = {
  styles: mapStyles,
  mapTypeControl: true,
  streetViewControl: false, // Remove Street View (man icon)
  fullscreenControl: true,
  zoomControl: true,
  minZoom: 3,
  maxZoom: 20,
  // Allow page scrolling when user scrolls outside the map
  // Only capture scroll events when actively interacting with the map
  gestureHandling: 'greedy',
  restriction: {
    latLngBounds: {
      north: 85,
      south: -85,
      west: -180,
      east: 180,
    },
    strictBounds: false,
  },
};
